import { z } from "zod";
import { TemplateCategory } from "./templateCategorySchema";

export const templateCreateSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["image", "video"], {
    required_error: "Type is required",
    invalid_type_error: "Type must be either image or video",
  }),
  description: z.string().optional(),
  categoryId: z.string().min(1, "Category is required"),
  zipFile: z
    .any()
    .refine((files) => files?.length > 0, "Zip file is required"),
  previewFile: z
    .any()
    .refine((files) => files?.length > 0, "Preview file is required")
    .refine((files) => {
      if (!files || files.length === 0) return true;
      const file = files[0];
      return file?.type?.startsWith("image/");
    }, "Preview file must be an image"),
});

export const templateUpdateSchema = z.object({
  name: z.string().min(1, "Name is required").optional(),
  type: z
    .enum(["image", "video"], {
      required_error: "Type is required",
      invalid_type_error: "Type must be either image or video",
    })
    .optional(),
  description: z.string().optional(),
  categoryId: z.string().min(1, "Category is required").optional(),
  zipFile: z.any().optional(),
  previewFile: z
    .any()
    .optional()
    .refine((files) => {
      if (!files || files.length === 0) return true;
      const file = files[0];
      return file?.type?.startsWith("image/");
    }, "Preview file must be an image"),
});

export type TemplateFormData = z.infer<typeof templateCreateSchema>;
export type TemplateUpdateFormData = z.infer<typeof templateUpdateSchema>;

export interface TemplateRequest {
  name: string;
  type: string;
  description?: string;
  categoryId: string;
  zipFile?: File;
  previewFile?: File;
}

export interface Template {
  id: string;
  name: string;
  type: string;
  description?: string;
  categoryId: string;
  category?: TemplateCategory;
  zipUrl: string;
  previewUrl: string;
  tenantId?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateResult {
  templates: Template[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface TemplateFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  categoryId?: string;
  type?: string;
}

export interface UpdateTemplatePayload {
  id: string;
  template: TemplateUpdateFormData;
}
